import * as React from 'react';
import MuiMenu from '@material-ui/core/Menu';

import { printWarning } from 'navigader/util';
import { useRandomString } from 'navigader/util/hooks';

import { Button, ButtonProps } from '../Button';
import { ValidIcon } from '../Icon';
import { List } from '../List';
import { PopoverOrigin } from '../Popover';
import { NestedMenuItem } from './NestedMenuItem';

/** ============================ Types ===================================== */
type MenuProps = {
  anchorOrigin?: PopoverOrigin;
  ButtonProps?: ButtonProps;
  icon?: ValidIcon;
  label?: React.ReactNode;
  transformOrigin?: PopoverOrigin;
};

type MenuExport = React.FC<MenuProps> & {
  Item: typeof List.Item;
  NestedItem: typeof NestedMenuItem;
};

/** ============================ Components ================================ */
export const Menu: MenuExport = (props) => {
  const {
    ButtonProps,
    children,
    icon,
    label,
    anchorOrigin = { vertical: 'bottom', horizontal: 'center' } as PopoverOrigin,
    transformOrigin = { vertical: 'top', horizontal: 'center' } as PopoverOrigin,
    ...rest
  } = props;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuId = useRandomString();

  // Only one of the `icon` and `label` props should be provided
  const hasBoth = Boolean(label && icon);
  const hasNeither = !Boolean(label || icon);
  if (hasBoth || hasNeither) {
    printWarning('`Menu` component expects one of `icon` or `label` prop');
  }

  const menuOpenerProps = {
    ...ButtonProps,
    ...rest,
    'aria-controls': menuId,
    'aria-haspopup': 'true' as React.AriaAttributes['aria-haspopup'],
    'onClick': handleClick,
  };

  const MenuOpener = icon ? (
    <Button {...menuOpenerProps} icon={icon} />
  ) : (
    <Button {...menuOpenerProps}>{label}</Button>
  );

  return (
    <>
      {MenuOpener}
      <MuiMenu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        children={children}
        getContentAnchorEl={null}
        id={menuId}
        open={!!anchorEl}
        onClose={handleClose}
        transformOrigin={transformOrigin}
      />
    </>
  );

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }
};

Menu.Item = List.Item;
Menu.NestedItem = NestedMenuItem;
